/**
 * cart-store.js
 *
 * Single point of entry for Shopify Cart API mutations.
 * Per CLAUDE.md Section 10: no component talks to /cart.js directly.
 *
 * Phase 4 ships the skeleton with mutation queue.
 * Phase 7 extends with Section Rendering API integration.
 *
 * Public API:
 *   cartStore.add(formData | object)  -> Promise<lineItem>
 *   cartStore.change(line, quantity)  -> Promise<cart>
 *   cartStore.update(updates)         -> Promise<cart>
 *   cartStore.clear()                  -> Promise<cart>
 *   cartStore.get()                    -> Promise<cart>
 *
 * Events dispatched on document:
 *   cart:updated               { cart }                    After every successful mutation OR reconciliation.
 *   cart:error                 { code, message, op }       After every failed mutation.
 *   cart:sections-rendered     { sections: string[] }      After SRA swap completes (Phase 7).
 *   cart:bundle-state-changed  { active: boolean, cart }   On edge transition into/out of bundle-active state (Phase 8).
 */

const CACHE_TTL_MS = 5000;
const REQUEST_TIMEOUT_MS = 10000;

const SRA_SECTIONS_BASE = 'cart-drawer,cart-main,cart-recommendations';

// On product pages, also re-render `product-buy-box` so the embedded
// <cart-bundle-upsell> card stays in sync with the live cart. We splice just
// the upsell element out of the response (see _swapPdpBundleUpsell) rather
// than swapping the whole section, which would clobber variant-picker /
// quantity / gallery state.
//
// Gated on the upsell element being present in the live DOM (not just on
// pathname): the snippet is only rendered when the product is a member of
// the `bundle-eligible` collection, so requesting the section on PDPs
// without an upsell is wasted bandwidth + extra dev-only icon CORS
// noise for HTML we'd splice nothing out of.
function getSraSections() {
  if (typeof document !== 'undefined' && document.querySelector('cart-bundle-upsell[data-context="pdp"]')) {
    return `${SRA_SECTIONS_BASE},product-buy-box`;
  }
  return SRA_SECTIONS_BASE;
}

const PROPERTY_KEYS = Object.freeze({
  BUNDLE_ID: '_bundle_id',
  BUNDLE_ROLE: '_bundle_role',
});

const BUNDLE_DISCOUNT_TITLE = (typeof window !== 'undefined' && window.LunchedBundleDiscountTitle) || 'Bundle discount';

// Rewrite absolute/protocol-relative CDN URLs in SRA-returned HTML to
// same-origin paths. Background: Liquid's `| asset_url` filter resolves to
// an absolute URL on the shop's CDN host (e.g.
// `//lunch-ed-dev.myshopify.com/cdn/shop/t/4/assets/icons.svg`). On the local
// dev preview at `127.0.0.1:9292` that URL is cross-origin, and the browser
// blocks SVG `<use href>` resolution against it - producing the
// "Unsafe attempt to load URL" warnings and visibly empty icon glyphs on
// elements brought in by an SRA swap. Initial page renders tolerate it (the
// SVG sprite gets fetched alongside the document); SRA-replaced fragments
// trigger fresh resolutions that fail. Rewriting to a path-only reference
// lets `shopify theme dev`'s `/cdn/*` proxy serve them same-origin. The
// rewrite is a no-op on production (where rendered URLs are already
// same-origin with the storefront).
const SAME_ORIGIN_ASSET_RE = /(\s(?:href|src|xlink:href)=)(["'])(?:https?:)?\/\/[^/]+(\/cdn\/[^"']+)\2/gi;
function rewriteAssetHostToSameOrigin(html) {
  if (typeof html !== 'string' || html.indexOf('/cdn/') === -1) return html;
  return html.replace(SAME_ORIGIN_ASSET_RE, '$1$2$3$2');
}

function logError(code, op, detail) {
  // Logger module (theme.js) is not exported as ESM yet.
  // Use console.error with the cart-store tag per CLAUDE Section 27.
  try {
    console.error('[cart-store]', code, { op, detail });
  } catch (_) { /* noop */ }
}

async function fetchWithTimeout(url, init, ms) {
  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), ms);
  try {
    const response = await fetch(url, { ...init, signal: controller.signal });
    return response;
  } finally {
    clearTimeout(timer);
  }
}

async function fetchCart() {
  const response = await fetchWithTimeout('/cart.js', {
    method: 'GET',
    headers: { 'Accept': 'application/json' },
    credentials: 'same-origin',
  }, REQUEST_TIMEOUT_MS);
  if (!response.ok) {
    const text = await response.text().catch(() => '');
    const err = new Error(text || `Failed to fetch cart: ${response.status}`);
    err.code = 'cart_fetch_failed';
    err.status = response.status;
    throw err;
  }
  return response.json();
}

function _mapDiscountErrorCode(data) {
  const message = ((data && (data.description || data.message)) || '').toLowerCase();
  if (message.includes('found, but invalid') || message.includes('not exist')) return 'discount_invalid';
  if (message.includes('expired')) return 'discount_expired';
  if (message.includes('minimum')) return 'discount_unmet_minimum';
  return 'discount_error';
}

class CartStore {
  constructor() {
    this._cache = null;
    this._cacheAt = 0;
    this._queue = [];
    this._processing = false;
    this.PROPERTY_KEYS = PROPERTY_KEYS;
    this._prevCart = null;

    // Optimistic-update layer (added 2026-06-29 per BUSINESS_RULES.md feel
    // requirement: cart mutations must update UI synchronously, with Shopify
    // as the eventual source of truth).
    this._localCart = null;     // shallow clone of `_cache` that we mutate optimistically
    this._seq = 0;              // monotonic mutation counter
    this._lastDispatchedSeq = 0;
    this._lastResolvedSeq = 0;  // highest sequence whose server response we've reconciled
    this._inFlightCount = 0;    // number of mutations currently waiting on Shopify
    this._pendingLineKeys = new Set();  // line keys with an in-flight or debounced mutation
    this._debounceTimers = new Map();   // lineKey -> setTimeout handle for change ops
    this._debounceTargets = new Map();  // lineKey -> latest target qty
    this._abortControllers = new Map(); // lineKey -> AbortController for in-flight requests
    this._flushResolvers = [];          // queued flush() promise resolvers
  }

  /**
   * Add a line item to the cart.
   * @param {FormData | object} payload - Form data or plain object with id, quantity, properties.
   * @returns {Promise<object>} The added line item.
   */
  add(payload) {
    return this._enqueue('add', () => this._add(payload));
  }

  /**
   * Add multiple line items atomically via /cart/add.js with the items[] shape.
   * @param {Array<object>} items - line item objects ({ id, quantity, properties }).
   * @returns {Promise<object>} The Shopify add.js response (items array).
   */
  addBatch(items) {
    if (!Array.isArray(items) || items.length === 0) {
      return Promise.resolve(this._prevCart);
    }
    return this._enqueue('add', () => this._addBatch(items));
  }

  /**
   * Change a line item's quantity. Optimistic: updates _localCart synchronously
   * and dispatches `cart:updated-optimistic` so the UI reflects the new state
   * immediately. The actual /cart/change.js POST is debounced 300ms per line
   * key so rapid +/- clicks collapse into a single server request with the
   * final qty.
   *
   * @param {string|number} line - line key (string) or 1-indexed line position
   * @param {number} quantity - target qty (0 removes the line)
   * @returns {Promise<object>} Resolves after the server reconciles. Useful
   *                            for flush() but components usually fire-and-forget.
   */
  change(line, quantity) {
    const isKey = typeof line === 'string';
    // Optimistic local update (string keys only - position-based change is
    // a rare legacy path that callers should avoid; fall through to enqueue).
    if (isKey && this._localCart) {
      this._localChangeQty(line, quantity);
      this._pendingLineKeys.add(line);
      this._dispatchOptimistic();
    }

    // Removes (qty <= 0) are one-shot intentional user actions, not a
    // rapid-click stream. Skip the 300ms debounce so the trash icon feels
    // immediate. Any subsequent qty-stepper changes for this key still
    // debounce normally.
    if (isKey && quantity <= 0) {
      // Cancel any pending debounced qty change for this line - the remove
      // supersedes it.
      const prev = this._debounceTimers.get(line);
      if (prev) {
        clearTimeout(prev);
        this._debounceTimers.delete(line);
        this._debounceTargets.delete(line);
      }
      return this._enqueue('change', () => this._change(line, 0), { lineKey: line });
    }

    // Debounce per line key for qty +/- streams. The actual POST fires 300ms
    // after the last change. New changes within 300ms reset the timer.
    if (isKey) {
      this._debounceTargets.set(line, quantity);
      const prev = this._debounceTimers.get(line);
      if (prev) clearTimeout(prev);
      return new Promise((resolve, reject) => {
        const handle = setTimeout(() => {
          this._debounceTimers.delete(line);
          const finalQty = this._debounceTargets.get(line);
          this._debounceTargets.delete(line);
          if (finalQty === undefined) {
            resolve(this._cache);
            return;
          }
          this._enqueue('change', () => this._change(line, finalQty), { lineKey: line })
            .then(resolve, reject);
        }, 300);
        this._debounceTimers.set(line, handle);
      });
    }

    // Non-key path (position): no debounce, no optimistic.
    return this._enqueue('change', () => this._change(line, quantity));
  }

  /**
   * Bulk-update cart line quantities.
   * @param {object} updates - { variantId: quantity, ... }
   */
  update(updates) {
    return this._enqueue('update', () => this._update(updates));
  }

  /**
   * Clear the cart.
   */
  clear() {
    return this._enqueue('clear', () => this._clear());
  }

  /**
   * Apply a Shopify discount code via /cart/update.js.
   * @param {string} code - the discount code to apply (empty string clears).
   */
  applyDiscount(code) {
    const trimmed = String(code || '').trim();
    return this._enqueue('discount', () => this._applyDiscount(trimmed));
  }

  /**
   * Remove the currently applied discount code.
   */
  removeDiscount() {
    return this.applyDiscount('');
  }

  /**
   * Returns true when the cart carries a discount_application whose title
   * matches the bundle discount title (BUNDLE_DISCOUNT_TITLE). Used by the
   * promo guard and the auto-remove-promo listener.
   * @param {object|null} cart - cart object from /cart.js (or null).
   * @returns {boolean}
   */
  isBundleActive(cart) {
    if (!cart || !Array.isArray(cart.items)) return false;
    for (var i = 0; i < cart.items.length; i++) {
      var line = cart.items[i];
      if (!Array.isArray(line.line_level_discount_allocations)) continue;
      for (var j = 0; j < line.line_level_discount_allocations.length; j++) {
        var alloc = line.line_level_discount_allocations[j];
        var app = alloc && alloc.discount_application;
        if (!app || !app.title) continue;
        // Match by prefix so the 6 native automatic discounts all match:
        // titles are `Bundle discount: 5% off` through `Bundle discount: 30% off`.
        if (app.title.indexOf(BUNDLE_DISCOUNT_TITLE) === 0) return true;
      }
    }
    return false;
  }

  /**
   * Emit cart:bundle-state-changed on edge transitions between bundle-active
   * and bundle-inactive states. No-op when neither side has changed.
   */
  _diffBundleState(prevCart, nextCart) {
    const prevActive = this.isBundleActive(prevCart);
    const nextActive = this.isBundleActive(nextCart);
    if (!prevActive && nextActive) {
      this._dispatch('cart:bundle-state-changed', { active: true, cart: nextCart });
    }
    if (prevActive && !nextActive) {
      this._dispatch('cart:bundle-state-changed', { active: false, cart: nextCart });
    }
  }

  /**
   * Synchronous snapshot of the most recent cart known to the store. Returns
   * the optimistic mirror when present (reflects in-flight mutations) else
   * the last server response. Returns null when neither has been populated
   * yet (very early page load). Callers needing a guaranteed-fresh cart
   * should use get() instead - this getter is for read-only UI that just
   * needs "what does the server roughly think the cart looks like".
   */
  getCached() {
    return this._localCart || this._cache || null;
  }

  /**
   * Get the current cart. Reads cache if fresh; else fetches.
   * NOT queued (read operation).
   */
  async get() {
    if (this._cache && (Date.now() - this._cacheAt) < CACHE_TTL_MS) {
      return this._cache;
    }
    const cart = await fetchCart();
    this._cache = cart;
    this._cacheAt = Date.now();
    this._syncLocalFromServer(cart);
    if (this._prevCart === null) {
      this._prevCart = cart;
    }
    return cart;
  }

  /**
   * Returns true while ANY mutation is still settling - pending debounce
   * timer, queued job, or in-flight request. Used to gate the secure-
   * checkout button so the customer can't navigate with stale state.
   */
  isBusy() {
    return (this._pendingLineKeys.size > 0)
      || (this._queue.length > 0)
      || (this._inFlightCount > 0)
      || (this._debounceTimers.size > 0);
  }

  /**
   * Flush all pending mutations. Resolves when every debounced timer has fired
   * AND every in-flight request has returned. Returns the canonical server cart.
   * Used by the secure-checkout button to guarantee the cart is fully synced
   * before the browser navigates.
   *
   * Timeout: 5s. If a server request hangs past that, we resolve with the last
   * known cache and let the navigation proceed (per user Q3 confirmation).
   */
  flush(timeoutMs) {
    const wait = (timeoutMs == null) ? 5000 : timeoutMs;
    return new Promise((resolve) => {
      // Trip every debounced timer immediately so its job enters the queue.
      this._debounceTimers.forEach((handle, key) => {
        clearTimeout(handle);
        const target = this._debounceTargets.get(key);
        this._debounceTimers.delete(key);
        this._debounceTargets.delete(key);
        if (target !== undefined) {
          this._enqueue('change', () => this._change(key, target));
        }
      });

      const settled = () => this._inFlightCount === 0 && this._queue.length === 0;
      if (settled()) {
        resolve(this._cache || this._localCart);
        return;
      }
      this._flushResolvers.push(resolve);

      // Safety timeout: proceed with last-known cache if requests hang.
      setTimeout(() => {
        if (this._flushResolvers.length === 0) return;
        const stragglers = this._flushResolvers.slice();
        this._flushResolvers.length = 0;
        stragglers.forEach((r) => r(this._cache || this._localCart));
      }, wait);
    });
  }

  /**
   * Subscribe to optimistic-cart updates. Returns the current cart snapshot
   * + a teardown function. Components use this when they want immediate UI
   * feedback without waiting for the server (cart drawer, cart page, cart
   * count badge).
   */
  onOptimisticUpdate(handler) {
    document.addEventListener('cart:updated-optimistic', handler);
    return () => document.removeEventListener('cart:updated-optimistic', handler);
  }

  // --- internals ---

  /**
   * Shallow-clone server cart into _localCart so we can mutate without
   * disturbing the server reference. We copy item-level fields needed for
   * optimistic math; everything else lives in _cache.
   */
  _syncLocalFromServer(cart) {
    if (!cart || !Array.isArray(cart.items)) {
      this._localCart = null;
      return;
    }
    this._localCart = {
      item_count: cart.item_count,
      original_total_price: cart.original_total_price,
      total_price: cart.total_price,
      total_discount: cart.total_discount,
      items: cart.items.map((line) => ({
        key: line.key,
        variant_id: line.variant_id,
        product_id: line.product_id,
        product_title: line.product_title,
        quantity: line.quantity,
        price: line.price,
        original_line_price: line.original_line_price,
        final_line_price: line.final_line_price,
        line_level_total_discount: line.line_level_total_discount,
        properties: line.properties,
        // Bundle-eligibility flag for client-side tier estimation. The Liquid
        // cart-line snippet stamps `data-bundle-eligible` from the product's
        // membership in the `bundle-eligible` collection; we mirror it onto
        // the local entry so optimistic math has access without re-querying
        // the DOM.
        bundle_eligible: this._readBundleEligibleFor(line),
      })),
    };
  }

  _readBundleEligibleFor(line) {
    // Try the DOM dataset (set by cart-line-item.liquid). If absent, default
    // to false - we'd rather under-discount and let server reconcile UP than
    // promise a discount that doesn't materialise.
    try {
      const el = document.querySelector(`cart-line[data-line-key="${line.key}"]`);
      if (el && el.dataset.bundleEligible) {
        return el.dataset.bundleEligible === 'true';
      }
    } catch (_) { /* CSS.escape edge case; fall through */ }
    return false;
  }

  /**
   * Apply an optimistic line-qty change to _localCart and re-derive totals.
   * Returns the snapshot AFTER the change (or null if the line isn't found).
   */
  _localChangeQty(lineKey, nextQty) {
    if (!this._localCart || !Array.isArray(this._localCart.items)) return null;
    const idx = this._localCart.items.findIndex((l) => l.key === lineKey);
    if (idx < 0) return null;
    const line = this._localCart.items[idx];
    if (nextQty <= 0) {
      this._localCart.items.splice(idx, 1);
    } else {
      // Update qty + recompute price totals from per-unit price.
      line.quantity = nextQty;
      line.original_line_price = line.price * nextQty;
      // final_line_price gets recomputed in _rederiveLocalTotals once we
      // know the eligible-box count and tier.
    }
    this._rederiveLocalTotals();
    return this._localCart;
  }

  /**
   * Recompute item_count, original_total_price, total_price, total_discount
   * on _localCart. The bundle discount is *estimated* client-side using the
   * tier table exposed at window.LunchedCartTiers (server-rendered by
   * theme.liquid). Server is authoritative; this estimate exists only so the
   * drawer feels instant.
   */
  _rederiveLocalTotals() {
    if (!this._localCart) return;
    const tiers = (typeof window !== 'undefined' && window.LunchedCartTiers) || [];
    let originalTotal = 0;
    let eligibleBoxCount = 0;
    let eligibleBoxOriginal = 0;
    let itemCount = 0;
    this._localCart.items.forEach((line) => {
      const lineOriginal = line.price * line.quantity;
      line.original_line_price = lineOriginal;
      originalTotal += lineOriginal;
      itemCount += line.quantity;
      if (line.bundle_eligible) {
        eligibleBoxCount += line.quantity;
        eligibleBoxOriginal += lineOriginal;
      }
    });
    // Look up tier pct for the eligible box count.
    let pct = 0;
    for (let i = 0; i < tiers.length; i++) {
      if (eligibleBoxCount >= tiers[i].qty) pct = tiers[i].pct;
    }
    const estimatedDiscount = Math.round(eligibleBoxOriginal * pct / 100);

    // Apply line-level final price for eligible lines using the estimated pct
    // so per-line strike-through display can use it. Non-eligible lines have
    // final == original.
    this._localCart.items.forEach((line) => {
      if (line.bundle_eligible && pct > 0) {
        line.final_line_price = Math.round(line.original_line_price * (100 - pct) / 100);
        line.line_level_total_discount = line.original_line_price - line.final_line_price;
      } else {
        line.final_line_price = line.original_line_price;
        line.line_level_total_discount = 0;
      }
    });

    this._localCart.item_count = itemCount;
    this._localCart.original_total_price = originalTotal;
    this._localCart.total_discount = estimatedDiscount;
    this._localCart.total_price = originalTotal - estimatedDiscount;
    this._localCart.estimated_bundle_pct = pct;
  }

  /**
   * Dispatch an optimistic update event so components can render from the
   * local mirror without waiting for the server. `pendingLineKeys` lets
   * components surface per-line loading state.
   */
  _dispatchOptimistic() {
    const seq = ++this._lastDispatchedSeq;
    this._dispatch('cart:updated-optimistic', {
      cart: this._localCart,
      pendingLineKeys: Array.from(this._pendingLineKeys),
      seq,
    });
  }

  _enqueue(op, fn, meta) {
    const seq = ++this._seq;
    return new Promise((resolve, reject) => {
      this._queue.push({ op, fn, resolve, reject, seq, lineKey: (meta && meta.lineKey) || null });
      this._processQueue();
    });
  }

  async _processQueue() {
    if (this._processing) return;
    if (this._queue.length === 0) {
      this._resolveFlushIfQuiet();
      return;
    }
    this._processing = true;

    const job = this._queue.shift();
    this._inFlightCount++;
    // Notify listeners (esp. the checkout-button gate) that a new request
    // is in flight - the prior `cart:updated` may have re-enabled the
    // button, but a fresh background mutation (e.g. auto-promo-removal
    // when a bundle activates) starts here and should re-disable.
    this._dispatch('cart:busy-changed', { busy: true });
    try {
      const result = await job.fn();
      this._lastResolvedSeq = job.seq;
      // SRA reconciliation: if the response carried section HTML, swap unless
      // a newer mutation is already queued or in-flight. Skipping the swap
      // when there's a successor prevents the user from seeing the cart flicker
      // back to a stale server snapshot before the newer response lands.
      const sections = result && typeof result === 'object' ? result.sections : null;
      const isLatest = this._queue.length === 0 && this._debounceTimers.size === 0;
      if (sections && isLatest) {
        this._swapSections(sections, job.op);
        this._dispatch('cart:sections-rendered', { sections: Object.keys(sections) });
      }
      // After successful mutation, fetch canonical cart and broadcast.
      const cart = await fetchCart();
      this._cache = cart;
      this._cacheAt = Date.now();
      // Reconcile optimistic mirror to the server truth. Only if there are no
      // newer mutations pending - else _localCart's tail-state is ahead of
      // this response, and overwriting would visually regress the user.
      if (isLatest) {
        this._syncLocalFromServer(cart);
      }
      this._diffBundleState(this._prevCart, cart);
      this._prevCart = cart;
      // Clear pending state BEFORE dispatching `cart:updated` so listeners
      // (especially the checkout-button state updater) observe
      // `isBusy() === false` once the mutation is settled. The
      // `_inFlightCount--` lives in the finally block below, but `finally`
      // runs AFTER the try block's dispatch - decrement here too so the
      // counter is accurate at dispatch time. The finally then guards
      // against double-decrement via a flag.
      if (job.lineKey) {
        this._pendingLineKeys.delete(job.lineKey);
      }
      this._inFlightCount--;
      job._counted = true;
      this._dispatch('cart:updated', { cart });
      // Only re-dispatch optimistic when the SRA swap DIDN'T already replace
      // the DOM. After a successful swap, the server-rendered HTML is
      // canonical (uses the shop's actual money format); re-running the
      // client-side renderer would overwrite the formatted amounts with our
      // peso-fallback formatter, causing currency-symbol flicker on dev.
      const didReconcileAuthoritatively = sections && isLatest;
      if (!didReconcileAuthoritatively) {
        this._dispatchOptimistic();
      }
      job.resolve(result);
    } catch (err) {
      const code = err.code || 'mutation_failed';
      const message = err.message || 'Unknown cart error';
      logError(code, job.op, message);
      // NOTE: job.reject is deferred to the bottom of this catch block so the
      // promise settles at the same time cart:error is dispatched. This keeps
      // callers that use `.finally()` for UI cleanup (e.g. Apply button
      // spinner) aligned with when the user-visible error message renders
      // (cart:error → _onError → _renderPromoError). If we rejected here, the
      // button would flip out of its loading state ~1s before the error UI
      // appeared - reads as a broken interaction.
      this._drainQueueWithError(job.op);
      try {
        const reconciled = await fetchCart();
        this._cache = reconciled;
        this._cacheAt = Date.now();
        // On failure, ALWAYS reconcile local mirror back to server. The
        // optimistic delta is whatever was wrong; server wins.
        this._syncLocalFromServer(reconciled);
        this._diffBundleState(this._prevCart, reconciled);
        this._prevCart = reconciled;
        // Clear pending + decrement in-flight BEFORE dispatching so the
        // button state updater sees `isBusy()===false`. Match finally's
        // guard with `_counted=true`.
        if (job.lineKey) this._pendingLineKeys.delete(job.lineKey);
        this._inFlightCount--;
        job._counted = true;
        // Refresh SRA sections so the drawer / cart page reflect the actual
        // server state. Partial-success failures (e.g. /cart/add.js returning
        // 422 "Only 50 of 70 added due to availability") DO mutate the cart
        // server-side but the success path never ran, so cart-drawer.liquid
        // and cart-main.liquid would otherwise still render their pre-error
        // HTML - the cart badge updates from cart:updated detail but the
        // drawer stays empty/stale. Best-effort: swallow any SRA failure
        // (the cart:updated event below still gives cart-aware components
        // something to render from).
        try {
          await this._refreshSectionsFromCart(job.op);
        } catch (sraErr) { /* best-effort */ }
        this._dispatchOptimistic();
        this._dispatch('cart:updated', { cart: reconciled });
      } catch (reconcileErr) {
        logError('cart_reconcile_failed', job.op, reconcileErr.message);
      }
      this._dispatch('cart:error', { code, message, op: job.op });
      job.reject({ code, message, op: job.op, status: err.status });
    } finally {
      // Decrement only if the success path didn't already do it (it
      // pre-decrements so listeners see `isBusy()===false` at dispatch).
      if (!job._counted) {
        this._inFlightCount--;
      }
      this._processing = false;
      if (this._queue.length > 0) {
        this._processQueue();
      } else {
        this._resolveFlushIfQuiet();
      }
    }
  }

  _resolveFlushIfQuiet() {
    if (this._flushResolvers.length === 0) return;
    if (this._inFlightCount > 0 || this._queue.length > 0 || this._debounceTimers.size > 0) return;
    const resolvers = this._flushResolvers.slice();
    this._flushResolvers.length = 0;
    resolvers.forEach((r) => r(this._cache || this._localCart));
  }

  _drainQueueWithError(triggeredBy) {
    while (this._queue.length > 0) {
      const j = this._queue.shift();
      j.reject({
        code: 'queue_aborted',
        message: 'Previous cart mutation failed; queue drained',
        op: j.op,
        triggered_by: triggeredBy,
      });
    }
  }

  _buildBody(payload, _op) {
    const sectionsUrl = (typeof window !== 'undefined' && window.location && window.location.pathname) || '/';
    const sections = getSraSections();
    if (payload instanceof FormData) {
      payload.append('sections', sections);
      payload.append('sections_url', sectionsUrl);
      return payload;
    }
    return JSON.stringify({ ...payload, sections, sections_url: sectionsUrl });
  }

  /**
   * Force-refresh the cart-aware sections via a standalone Section Rendering
   * API call. Used in the error path of _processQueue when the failed
   * mutation may still have mutated the server cart (partial-success 422s)
   * and the in-DOM section HTML has gone stale. Throws on network / parse
   * error so the caller can swallow.
   *
   * The URL MUST be `/` (root). Shopify intercepts certain route prefixes
   * (`/pages/<handle>?sections=...`, `/cart?sections=...`, `/products?sections=...`)
   * and returns the resource's JSON metadata instead of the rendered section
   * HTML, which silently bypasses _swapSections. The homepage layout renders
   * cart-drawer (via theme.liquid) so it works as a universal SRA entry point.
   */
  async _refreshSectionsFromCart(op) {
    const sections = getSraSections();
    const url = '/?sections=' + encodeURIComponent(sections);
    const response = await fetchWithTimeout(url, {
      method: 'GET',
      credentials: 'same-origin',
      headers: { 'Accept': 'application/json' },
    }, REQUEST_TIMEOUT_MS);
    if (!response.ok) throw new Error('SRA refresh failed: ' + response.status);
    const data = await response.json();
    if (data && typeof data === 'object') {
      this._swapSections(data, op);
      this._dispatch('cart:sections-rendered', { sections: Object.keys(data) });
    }
  }

  _swapSections(sections, op) {
    if (!sections || typeof sections !== 'object') return;
    // Capture cart-drawer dialog open state BEFORE swap. `target.replaceWith()`
    // on the <cart-drawer> wrapper replaces the dialog inside it; the freshly-
    // rendered <dialog> has no `open` attribute, so the drawer collapses on
    // every cart mutation while open without an explicit restore.
    const drawerWasOpen = document.querySelector('cart-drawer dialog')?.open === true;

    // Capture per-line qty-stepper input values BEFORE swap, keyed by
    // data-variant-id (line keys rotate on every change but variant ids are
    // stable). Only relevant for 'change' ops: rapid clicks can have the
    // user's local input AHEAD of the server's view (server is still
    // processing m1 while the user has already clicked to m2's target);
    // without preserving, the post-swap DOM shows the server's intermediate
    // value and the user's later clicks appear to "revert". For other ops
    // (add/remove/clear), the input value reflects server state by
    // definition, so any pre/post divergence is the legitimate server
    // update - never user intent - and we skip catch-up.
    const pendingQtyByVariantId = new Map();
    if (op === 'change') {
      document.querySelectorAll('cart-line[data-variant-id]').forEach((line) => {
        const input = line.querySelector('qty-stepper input[type="number"]');
        if (!input) return;
        const v = parseInt(input.value, 10);
        if (!isNaN(v) && v > 0) pendingQtyByVariantId.set(line.dataset.variantId, v);
      });
    }

    Object.entries(sections).forEach(([name, html]) => {
      if (typeof html !== 'string' || html.trim().length === 0) return;
      const normalized = rewriteAssetHostToSameOrigin(html);
      if (name === 'product-buy-box') {
        this._swapPdpBundleUpsell(normalized);
        return;
      }
      const target = document.querySelector(`[data-section-id="${name}"]`);
      if (!target) return;
      const wrapper = document.createElement('div');
      wrapper.innerHTML = normalized.trim();
      const fresh = wrapper.firstElementChild;
      if (!fresh) return;

      // When the cart drawer is open mid-mutation, preserve the live <dialog>
      // element and replace only its inner children. Replacing the whole
      // <cart-drawer> would re-mount the dialog; the browser then sees a
      // newly-`[open]` element and re-runs the `@starting-style` entry
      // transition (cart.css:42), making the drawer slide in from the right
      // again on every quantity / remove / discount click while open.
      // The wrapper element's attributes (data-open-on-add) come from theme
      // settings and don't change at runtime, so preserving it is safe.
      if (name === 'cart-drawer' && drawerWasOpen) {
        const liveDialog = target.querySelector('dialog.cart-drawer__dialog');
        const freshDialog = fresh.querySelector('dialog.cart-drawer__dialog');
        if (liveDialog && freshDialog) {
          liveDialog.replaceChildren(...freshDialog.childNodes);
          return;
        }
      }

      target.replaceWith(fresh);
    });

    if (drawerWasOpen) {
      // Fallback for the closed-dialog / missing-dialog path above: if the
      // section was swapped wholesale (drawer was closed, or the targeted
      // preserve path didn't apply), the fresh dialog needs showModal() to
      // restore its open state.
      const dialog = document.querySelector('cart-drawer dialog');
      if (dialog && !dialog.open) {
        try { dialog.showModal(); } catch (_) { /* dialog removed; ignore */ }
        document.documentElement.style.overflow = 'hidden';
      }
    }

    // Catch-up: for each line where the user's local input diverged from
    // the server's response, restore the local value and fire a follow-up
    // mutation. Handles both directions (clicking up faster than mutations
    // complete; clicking down faster). The follow-up uses the new (rotated)
    // line key. Loops terminate naturally: the catch-up's own SRA swap will
    // restore equality once the server catches up.
    this._lastQtySnapByVariant = this._lastQtySnapByVariant || new Map();
    pendingQtyByVariantId.forEach((pendingQty, variantId) => {
      const newLine = document.querySelector(`cart-line[data-variant-id="${CSS.escape(variantId)}"]`);
      if (!newLine) return;
      const newInput = newLine.querySelector('qty-stepper input[type="number"]');
      if (!newInput) return;
      const serverQty = parseInt(newInput.value, 10);
      const max = parseInt(newInput.getAttribute('max') || '99', 10);
      const min = parseInt(newInput.getAttribute('min') || '1', 10);
      const targetQty = Math.max(min, Math.min(pendingQty, max));
      const stepper = newInput.closest('qty-stepper');
      if (targetQty === serverQty) {
        this._lastQtySnapByVariant.delete(variantId);
        return;
      }
      // Cap-loop guard: if the previous swap also saw the SAME (pendingQty,
      // serverQty) pair and we already fired a catch-up change(), the server
      // is enforcing a cap below the user's intent. Restoring + firing again
      // would loop forever (change(51) -> server returns 50 -> restore 51 ->
      // change(51) -> ...). Trust the server, sync input to serverQty, drop
      // the optimistic mirror entry.
      const lastSnap = this._lastQtySnapByVariant.get(variantId);
      if (lastSnap && lastSnap.pendingQty === pendingQty && lastSnap.serverQty === serverQty) {
        newInput.value = String(serverQty);
        // ALSO lock the stepper's max to the server's verdict. Liquid's
        // `variant.inventory_quantity` can disagree with the actual /cart/change.js
        // enforcement cap (different location, Function-imposed limit, per-customer
        // rule, etc.). Without lowering the stepper max, the + button would stay
        // enabled and every subsequent click would re-trigger the same cap loop.
        newInput.setAttribute('max', String(serverQty));
        if (stepper) {
          stepper.dataset.max = String(serverQty);
          if (typeof stepper.max === 'number') stepper.max = serverQty;
        }
        if (stepper && typeof stepper._syncDisabledState === 'function') stepper._syncDisabledState();
        this._lastQtySnapByVariant.delete(variantId);
        // Also clean the optimistic mirror so a follow-up cart:updated doesn't
        // try to re-render this line with the stale pending value.
        if (this._localCart && Array.isArray(this._localCart.items)) {
          this._localCart.items.forEach((it) => {
            if (String(it.variant_id) === String(variantId)) it.quantity = serverQty;
          });
        }
        return;
      }
      this._lastQtySnapByVariant.set(variantId, { pendingQty, serverQty });
      newInput.value = String(targetQty);
      if (stepper && typeof stepper._syncDisabledState === 'function') stepper._syncDisabledState();
      this.change(newLine.dataset.lineKey, targetQty).catch(() => {});
    });
  }

  // Splice just the <cart-bundle-upsell> element out of the freshly-rendered
  // product-buy-box response and replace the live one. Preserves variant
  // picker selection, quantity input, image gallery, and any other ephemeral
  // buy-box state while still giving the upsell card fresh server-rendered
  // dataset values (eligible-qty + first-line) post-mutation.
  //
  // Uses DOMParser (not `div.innerHTML = ...`) because the buy-box HTML
  // contains many <svg><use href="..."/></svg> sprite refs; assigning to
  // innerHTML eagerly resolves those, which on the local dev preview triggers
  // cross-origin warnings (the rendered URLs point at the shop CDN host).
  // DOMParser's inert document suppresses resource loading per spec.
  _swapPdpBundleUpsell(html) {
    const doc = new DOMParser().parseFromString(html, 'text/html');
    const fresh = doc.querySelector('cart-bundle-upsell[data-context="pdp"]');
    const live = document.querySelector('cart-bundle-upsell[data-context="pdp"]');
    if (fresh && live) live.replaceWith(document.adoptNode(fresh));
  }

  async _add(payload) {
    const init = {
      method: 'POST',
      credentials: 'same-origin',
      headers: { 'Accept': 'application/json' },
    };
    const body = this._buildBody(payload, 'add');
    if (body instanceof FormData) {
      init.body = body;
    } else {
      init.headers['Content-Type'] = 'application/json';
      init.body = body;
    }
    const response = await fetchWithTimeout('/cart/add.js', init, REQUEST_TIMEOUT_MS);
    const data = await response.json().catch(() => ({}));
    if (!response.ok) {
      const err = new Error(data.description || data.message || `Add failed: ${response.status}`);
      err.code = data.status || 'add_failed';
      err.status = response.status;
      throw err;
    }
    return data;
  }

  async _addBatch(items) {
    // Shopify quirks on POST /cart/add.js with `{items: [...]}` shape:
    //   1. It REFUSES to merge with an existing cart line of the same variant
    //      - 422s with "max already in cart" regardless of inventory.
    //   2. It enforces a HARD CAP of 50 items added per call, regardless of
    //      requested qty or actual stock (verified empirically: requesting
    //      200 of a variant with 100+ stock lands exactly 50).
    // The single-item shape `{id, quantity}` has NEITHER issue: it auto-merges
    // with existing lines AND has no per-call cap. So we always send items
    // individually via the single shape. Trade-off: N sequential POSTs vs
    // 1 batch POST. For BAS commits this is typically 2-5 round trips, which
    // is well worth predictable behavior. The last POST's response carries
    // the SRA sections that _processMutation needs to swap the drawer + cart
    // page, so we return that one.
    let lastResp = null;
    for (let i = 0; i < items.length; i++) {
      const item = items[i];
      const payload = { id: item.id, quantity: item.quantity };
      if (item.properties) payload.properties = item.properties;
      const response = await fetchWithTimeout('/cart/add.js', {
        method: 'POST',
        credentials: 'same-origin',
        headers: { 'Accept': 'application/json', 'Content-Type': 'application/json' },
        body: this._buildBody(payload, 'add'),
      }, REQUEST_TIMEOUT_MS);
      const data = await response.json().catch(() => ({}));
      if (!response.ok) {
        const err = new Error(data.description || data.message || `Add failed: ${response.status}`);
        err.code = data.status || 'add_failed';
        err.status = response.status;
        throw err;
      }
      lastResp = data;
    }
    return lastResp || {};
  }

  async _change(line, quantity) {
    const payload = (typeof line === 'number') ? { line, quantity } : { id: line, quantity };
    // Guard against stale line keys. Rapid clicks on the upsell "Add" CTA or
    // the qty-stepper after a remove can queue mutations with a key that the
    // prior mutation already rotated. Shopify responds with 400 "no valid id
    // or line parameter" - misleading; the id is present, just rotated.
    // When detected: re-fetch cart, look up the line by variant_id, and retry
    // with the current key. Surfaces as a normal change with sections so SRA
    // reconciles correctly. (Previous version silently returned _cache without
    // sections, which left the optimistic UI orphaned.)
    if (typeof payload.id === 'string' && this._cache && Array.isArray(this._cache.items)) {
      const stillExists = this._cache.items.some(i => i.key === payload.id);
      if (!stillExists) {
        // Resolve to a current key via variant_id, if we can find one.
        // The stale key format is "<variant_id>:<hash>". Extract the variant.
        const colonIdx = payload.id.indexOf(':');
        const variantPart = colonIdx > 0 ? payload.id.slice(0, colonIdx) : payload.id;
        let currentLine = null;
        if (variantPart) {
          currentLine = this._cache.items.find(i => String(i.variant_id) === String(variantPart));
        }
        if (currentLine && currentLine.key !== payload.id) {
          // Retry with the current key - same intent, fresh address.
          payload.id = currentLine.key;
        } else {
          // No matching line found - the user likely removed it elsewhere or
          // the cart drained. Fall through to send the original payload; the
          // server will 400 and our error handler will reconcile the cart.
        }
      }
    }
    const response = await fetchWithTimeout('/cart/change.js', {
      method: 'POST',
      credentials: 'same-origin',
      headers: { 'Accept': 'application/json', 'Content-Type': 'application/json' },
      body: this._buildBody(payload, 'change'),
    }, REQUEST_TIMEOUT_MS);
    const data = await response.json().catch(() => ({}));
    if (!response.ok) {
      const err = new Error(data.description || `Change failed: ${response.status}`);
      err.code = data.status || 'change_failed';
      err.status = response.status;
      throw err;
    }
    // Detect inventory cap. Shopify returns 200 with the line's quantity
    // silently clamped to available inventory when `inventory_policy=deny` and
    // requested qty > inventory_quantity. The server-rendered qty-stepper max
    // (via snippets/qty-stepper.liquid + line.variant) prevents this for
    // sighted users, but the inventory could change between page render and
    // click (another customer buys the last unit), so we keep the fallback.
    if (typeof payload.id === 'string' && typeof payload.quantity === 'number' && data && Array.isArray(data.items)) {
      const capped = data.items.find(i => i.key === payload.id);
      if (capped && capped.quantity < payload.quantity) {
        const template = (typeof window !== 'undefined' && window.LunchedCartMessages && window.LunchedCartMessages.inventory_capped) ||
                         'Only __COUNT__ of __TITLE__ in stock.';
        const message = template
          .replace('__COUNT__', String(capped.quantity))
          .replace('__TITLE__', capped.product_title || capped.title || 'this item');
        this._dispatch('cart:error', {
          code: 'inventory_capped',
          message,
          op: 'change',
          capped_to: capped.quantity,
          requested: payload.quantity,
          line_key: payload.id,
        });
      }
    }
    return data;
  }

  async _update(updates) {
    const response = await fetchWithTimeout('/cart/update.js', {
      method: 'POST',
      credentials: 'same-origin',
      headers: { 'Accept': 'application/json', 'Content-Type': 'application/json' },
      body: this._buildBody({ updates }, 'update'),
    }, REQUEST_TIMEOUT_MS);
    const data = await response.json().catch(() => ({}));
    if (!response.ok) {
      const err = new Error(data.description || `Update failed: ${response.status}`);
      err.code = data.status || 'update_failed';
      err.status = response.status;
      throw err;
    }
    return data;
  }

  async _clear() {
    const response = await fetchWithTimeout('/cart/clear.js', {
      method: 'POST',
      credentials: 'same-origin',
      headers: { 'Accept': 'application/json', 'Content-Type': 'application/json' },
      body: this._buildBody({}, 'clear'),
    }, REQUEST_TIMEOUT_MS);
    const data = await response.json().catch(() => ({}));
    if (!response.ok) {
      const err = new Error(data.description || `Clear failed: ${response.status}`);
      err.code = 'clear_failed';
      err.status = response.status;
      throw err;
    }
    return data;
  }

  async _applyDiscount(code) {
    const response = await fetchWithTimeout('/cart/update.js', {
      method: 'POST',
      credentials: 'same-origin',
      headers: { 'Accept': 'application/json', 'Content-Type': 'application/json' },
      body: this._buildBody({ discount: code }, 'discount'),
    }, REQUEST_TIMEOUT_MS);
    const data = await response.json().catch(() => ({}));
    if (!response.ok) {
      const err = new Error(data.description || data.message || `Discount apply failed: ${response.status}`);
      err.code = _mapDiscountErrorCode(data);
      err.status = response.status;
      throw err;
    }
    // Shopify returns 2xx even for invalid codes, storing them with applicable: false.
    // Surface that as cart:error so the UI can render an inline message. For
    // applicable: true, trust Shopify's validator — including shipping-type
    // discounts which produce no cart-level allocation (they only allocate at
    // checkout against the chosen shipping method, so an empty
    // line_level_discount_allocations / cart_level_discount_applications is
    // normal, not a zombie).
    if (code && Array.isArray(data.discount_codes)) {
      const submitted = code.trim().toLowerCase();
      const matching = data.discount_codes.find((d) => d && d.code && String(d.code).toLowerCase() === submitted);
      if (matching && matching.applicable === false) {
        const err = new Error('Discount code is not applicable');
        err.code = 'discount_invalid';
        err.status = 200;
        throw err;
      }
    }
    return data;
  }

  _dispatch(name, detail) {
    document.dispatchEvent(new CustomEvent(name, { detail, bubbles: false }));
  }
}

const cartStore = new CartStore();
export default cartStore;

// Also expose on window for non-module consumers (e.g. inline scripts in Liquid).
if (typeof window !== 'undefined') {
  window.cartStore = cartStore;
}

// Seed the local cart mirror on page load so the FIRST mutation already has
// optimistic state available. Also dispatch `cart:updated` after the initial
// fetch resolves so cart-aware listeners (PDP inventory cap, BAS cart-aware
// max, etc.) get a chance to apply themselves against the live cart state.
// Without this dispatch, a component whose connectedCallback ran BEFORE
// the initial fetch resolved never re-renders and sees an empty cart
// indefinitely (until a mutation happens).
function seedCart() {
  cartStore.get().then((cart) => {
    if (cart) cartStore._dispatch('cart:updated', { cart });
  }).catch(() => {});
}
if (typeof document !== 'undefined') {
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', seedCart, { once: true });
  } else {
    seedCart();
  }
}

// Phase 8: auto-remove a manual promo code when a bundle activates.
// Bundle and promo discounts cannot stack (BUSINESS_RULES.md), so the bundle
// wins and the promo is removed with an aria-live announcement.
if (typeof document !== 'undefined') {
  document.addEventListener('cart:bundle-state-changed', function (e) {
    if (!e || !e.detail) return;
    if (!e.detail.active) return;
    const cart = e.detail.cart;
    if (!cart || !Array.isArray(cart.discount_codes)) return;
    // Trigger on ANY code present, regardless of applicable status. When
    // bundle activates with combinations disabled on the promo (typical),
    // Shopify auto-flips applicable: false but the code itself stays in the
    // cart as a zombie entry. BUSINESS_RULES.md mandates we remove it AND
    // surface the canonical message - so we don't filter by applicable here.
    if (cart.discount_codes.length === 0) return;
    if (!window.cartStore || typeof window.cartStore.removeDiscount !== 'function') return;
    window.cartStore.removeDiscount().then(function () {
      const msg = (window.LunchedCartMessages && window.LunchedCartMessages.promo_removed_for_bundle)
        || 'Your promo code was removed because bundle discounts cannot be combined with promo codes.';
      document.dispatchEvent(new CustomEvent('cart:error', {
        detail: { code: 'promo_removed_for_bundle', message: msg, op: 'remove_discount' }
      }));
    }).catch(function () { /* error broadcast via cart:error from store */ });
  });
}

// =============================================================================
// Optimistic DOM renderer
//
// Subscribes to `cart:updated-optimistic` and updates cart drawer + cart page
// DOM in place from the local cart mirror. The full SRA section swap still
// runs after the server response; this renderer just makes the in-between
// frame feel instant.
//
// Selectors used (added 2026-06-29):
//   cart-line[data-line-key]                  per-line root
//   [data-cart-line-price-original]           pre-discount line price
//   [data-cart-line-price-final]              post-discount line price (if shown)
//   [data-cart-subtotal]                      cart subtotal value
//   [data-cart-discount-row]                  bundle discount row container
//   [data-cart-discount-pct]                  "(N% off)" pill inside discount row
//   [data-cart-discount-value]                discount amount value
//   [data-cart-shipping-row]                  shipping row container (hidden when unlocked)
//   [data-cart-shipping]                      shipping value
//   [data-cart-total]                         estimated total / total
//   [data-cart-total-label]                   total row label
//   [data-cart-title-with-count]              drawer "Bag (N)" heading
//   [data-cart-main-count]                    cart page "(N)" count badge
//   [data-line-pending] (added/removed)       per-line loading state
// =============================================================================

if (typeof document !== 'undefined') {
  const formatMoney = function (cents) {
    if (window.Shopify && typeof Shopify.formatMoney === 'function') {
      try { return Shopify.formatMoney(cents); } catch (_) { /* fall through */ }
    }
    // Fallback: peso sign + integer-pesos. Mirrors `| money_without_trailing_zeros`.
    return '₱' + Math.round((cents || 0) / 100).toLocaleString('en-US');
  };

  const ENV_THRESHOLD_CENTS = function () {
    // Hidden on every cart-totals render via data-cart-shipping-row's parent;
    // free-shipping threshold mirrors settings.cart_free_shipping_threshold.
    // We don't need the actual value here since the toggle is driven by the
    // optimistic total - just reuse whatever was last rendered.
    const row = document.querySelector('[data-cart-shipping-row]');
    if (!row) return 250000;
    const val = row.querySelector('[data-cart-shipping]');
    return val ? 250000 : 250000; // placeholder; not used today
  };

  function renderLine(line) {
    const escapedKey = (typeof CSS !== 'undefined' && CSS.escape) ? CSS.escape(line.key) : line.key;
    document.querySelectorAll(`cart-line[data-line-key="${escapedKey}"]`).forEach((el) => {
      // Qty input (inside qty-stepper)
      const input = el.querySelector('qty-stepper input[type="number"]');
      if (input && parseInt(input.value, 10) !== line.quantity) {
        input.value = String(line.quantity);
        const stepper = input.closest('qty-stepper');
        if (stepper && typeof stepper._syncDisabledState === 'function') {
          stepper._syncDisabledState();
        }
      }
      // Original price + (if discounted) final price
      const originalEl = el.querySelector('[data-cart-line-price-original]');
      if (originalEl) originalEl.textContent = formatMoney(line.original_line_price);
      const finalEl = el.querySelector('[data-cart-line-price-final]');
      if (finalEl) {
        if (line.final_line_price < line.original_line_price) {
          finalEl.textContent = formatMoney(line.final_line_price);
          finalEl.hidden = false;
        } else {
          // No discount on this line - hide the final price element so
          // strike-through isn't shown for no reason. (The Liquid render
          // path doesn't emit this element when un-discounted; if we're
          // here in optimistic mode and the discount disappeared, hide.)
          finalEl.hidden = true;
        }
      }
    });
  }

  function renderTotals(cart) {
    // Subtotal (always pre-discount original total)
    document.querySelectorAll('[data-cart-subtotal]').forEach((el) => {
      el.textContent = formatMoney(cart.original_total_price);
      el.style.textDecoration = cart.total_discount > 0 ? 'line-through' : 'none';
    });
    // Discount row + value + pct pill
    const showDiscount = cart.total_discount > 0;
    document.querySelectorAll('[data-cart-discount-row]').forEach((el) => {
      el.hidden = !showDiscount;
    });
    document.querySelectorAll('[data-cart-discount-value]').forEach((el) => {
      el.textContent = formatMoney(cart.total_discount);
    });
    // Optimistic pct pill: only overwrite if we're confident the applied
    // discount is a bundle tier. The label stays whatever Liquid last
    // rendered (data-cart-discount-label); on server response, the SRA
    // section swap refreshes it from the accurate title/pct. Hiding the
    // pill when pct is 0 prevents the stale "(0% off)" bug we hit when a
    // non-bundle discount wins Shopify's best-value selection.
    document.querySelectorAll('[data-cart-discount-pct]').forEach((el) => {
      const pct = cart.estimated_bundle_pct != null ? cart.estimated_bundle_pct : 0;
      const labelEl = el.closest('.cart-totals__row-label')?.querySelector('[data-cart-discount-label]');
      const currentLabel = labelEl ? labelEl.textContent.trim() : '';
      const isBundleLabel = currentLabel.indexOf(BUNDLE_DISCOUNT_TITLE) === 0;
      if (pct > 0 && isBundleLabel) {
        el.textContent = '(' + pct + '% off)';
        el.hidden = false;
      } else {
        el.hidden = true;
      }
    });
    // Total = total_price + shipping (shipping read from existing row value)
    document.querySelectorAll('[data-cart-shipping]').forEach((el) => {
      // shipping is unaffected by qty change - don't touch its number
    });
    document.querySelectorAll('[data-cart-total]').forEach((el) => {
      // Total = cart.total_price (post-discount) + the visible shipping value.
      const shippingEl = el.closest('.cart-totals')?.querySelector('[data-cart-shipping]');
      let shipping = 0;
      if (shippingEl) {
        const raw = shippingEl.textContent || '';
        // Strip currency symbols + commas for numeric parse.
        const digits = raw.replace(/[^\d.]/g, '');
        shipping = digits ? Math.round(parseFloat(digits) * 100) : 0;
      }
      el.textContent = formatMoney(cart.total_price + shipping);
    });
  }

  function renderHeadings(cart) {
    const n = cart.item_count;
    // Drawer "Bag (N)" - use the locale template if exposed; else inline rewrite.
    document.querySelectorAll('[data-cart-title-with-count]').forEach((el) => {
      // The Liquid render uses `cart.title_with_count | t: count: N` - we'll
      // just replace the (N) parenthetical heuristically.
      el.textContent = (el.textContent || '').replace(/\(\d+\)/, '(' + n + ')')
        || el.textContent;
      // If no parens were there (e.g. localized differently), append.
      if (!/\(\d+\)/.test(el.textContent)) {
        el.textContent = el.textContent.replace(/\s*$/, '') + ' (' + n + ')';
      }
    });
    // Cart page "(N)" count badge
    document.querySelectorAll('[data-cart-main-count]').forEach((el) => {
      el.textContent = '(' + n + ')';
      el.hidden = n === 0;
    });
    // Header cart icon bubble (the existing `[data-cart-count]` attribute on
    // the badge). The header-cart-icon CE also listens to `cart:updated` for
    // the SoT reconciliation; the line below makes the badge feel instant.
    document.querySelectorAll('[data-cart-count]').forEach((el) => {
      el.textContent = String(n);
      el.hidden = n === 0;
    });
  }

  function renderPendingState(pendingKeys) {
    const pending = new Set(pendingKeys || []);
    document.querySelectorAll('cart-line[data-line-key]').forEach((el) => {
      const key = el.dataset.lineKey;
      if (pending.has(key)) el.setAttribute('data-line-pending', 'true');
      else el.removeAttribute('data-line-pending');
    });
  }

  function reapeCartLinesNotIn(cart) {
    // Optimistic remove: if a cart-line element exists in DOM but its key
    // isn't in cart.items, the user just removed it. Detach the element so
    // it disappears immediately (the SRA swap will overwrite the whole
    // drawer ~200ms later anyway, but this prevents a "line dims and then
    // sits there" visual artifact when the swap is delayed).
    const liveKeys = new Set((cart.items || []).map((l) => l.key));
    document.querySelectorAll('cart-line[data-line-key]').forEach((el) => {
      if (!liveKeys.has(el.dataset.lineKey)) {
        el.style.transition = 'opacity 0.15s';
        el.style.opacity = '0';
        setTimeout(() => { if (el.parentNode) el.remove(); }, 160);
      }
    });
  }

  function applyEmptyCartState(cart) {
    // When the cart goes empty optimistically (user trashed the last line),
    // the SRA swap will eventually render the empty-cart UI from
    // sections/cart-drawer.liquid - but until that response arrives (200-
    // 400ms later) the upsell card + promo input + totals strip stay
    // visible, showing a confusing intermediate state (Bag (0) with
    // upsell still pitching, "estimated total" with stale numbers).
    // Hide all non-empty-state elements while we wait for the swap.
    // The SRA swap will replace the whole drawer body anyway, restoring
    // server-rendered truth (empty placeholder when empty, full layout
    // when not).
    const isEmpty = (cart.item_count || 0) === 0;
    document.querySelectorAll('cart-drawer cart-bundle-upsell, cart-main cart-bundle-upsell').forEach((el) => {
      if (isEmpty) el.setAttribute('data-bas-hidden-empty', '');
      else el.removeAttribute('data-bas-hidden-empty');
    });
    document.querySelectorAll('cart-drawer cart-discount, cart-main cart-discount').forEach((el) => {
      if (isEmpty) el.setAttribute('data-bas-hidden-empty', '');
      else el.removeAttribute('data-bas-hidden-empty');
    });
    document.querySelectorAll('cart-drawer .cart-totals, cart-main .cart-totals').forEach((el) => {
      if (isEmpty) el.setAttribute('data-bas-hidden-empty', '');
      else el.removeAttribute('data-bas-hidden-empty');
    });
  }

  function updateCheckoutButtonState() {
    // Disable the secure-checkout button while any mutation is unresolved,
    // OR when the cart is empty. Prevents the customer clicking through to
    // checkout with stale state mid-flight (per requirement #14: "Disable
    // the Checkout button only while pending cart mutations are being
    // flushed").
    const cs = window.cartStore;
    if (!cs) return;
    const cart = cs._localCart || cs._cache;
    const isEmpty = !cart || (cart.item_count || 0) === 0;
    const busy = cs.isBusy && cs.isBusy();
    document.querySelectorAll('[data-cart-checkout-button]').forEach((btn) => {
      btn.disabled = isEmpty || busy;
      btn.classList.toggle('is-flushing', !!busy && !isEmpty);
    });
  }

  document.addEventListener('cart:updated-optimistic', function (e) {
    const detail = e && e.detail;
    if (!detail || !detail.cart) return;
    const cart = detail.cart;
    reapeCartLinesNotIn(cart);
    (cart.items || []).forEach(renderLine);
    renderTotals(cart);
    renderHeadings(cart);
    renderPendingState(detail.pendingLineKeys || []);
    applyEmptyCartState(cart);
    updateCheckoutButtonState();
  });
  // Also update button state after SRA reconciliation (cart:updated fires
  // post-server-response). The SRA swap replaces the button DOM but the
  // server-rendered HTML always has `disabled` cleared - if we landed here
  // with no pending mutations, that's correct. If there's still a pending
  // mutation (rare race), we re-disable.
  document.addEventListener('cart:updated', updateCheckoutButtonState);
  // And on cart-error - reconciliation may have flipped state.
  document.addEventListener('cart:error', updateCheckoutButtonState);
  // Background mutation just started (e.g. auto-promo-removal). Re-evaluate.
  document.addEventListener('cart:busy-changed', updateCheckoutButtonState);

  // Surface inventory_capped errors back into the optimistic UI: revert the
  // line qty to whatever the server reconciled to, and announce via the
  // cart-live-alert region (per user Q2 confirmation).
  document.addEventListener('cart:error', function (e) {
    const detail = e && e.detail;
    if (!detail || detail.code !== 'inventory_capped') return;
    const alert = document.getElementById('cart-live-alert');
    if (alert) {
      alert.textContent = '';
      setTimeout(function () { alert.textContent = detail.message || ''; }, 50);
    }
  });

  // Pre-checkout flush. The secure-checkout button is a regular form submit;
  // intercept, await cartStore.flush() so every pending debounce + in-flight
  // request settles, then re-submit. Disable the button + add is-flushing
  // class for visual feedback only during the flush window.
  document.addEventListener('submit', function (e) {
    const form = e.target;
    if (!form || !form.matches('[data-cart-checkout-form]')) return;
    if (form.dataset.cartFlushed === 'true') {
      // Re-entry after flush - allow native submit.
      return;
    }
    e.preventDefault();
    if (!window.cartStore || typeof window.cartStore.flush !== 'function') {
      form.submit();
      return;
    }
    const btn = form.querySelector('[data-cart-checkout-button]');
    if (btn) {
      btn.disabled = true;
      btn.classList.add('is-flushing');
    }
    window.cartStore.flush().then(function () {
      if (btn) {
        btn.classList.remove('is-flushing');
        btn.disabled = false;
      }
      form.dataset.cartFlushed = 'true';
      form.submit();
    }).catch(function () {
      // Even on flush rejection (timeout), proceed to checkout - Shopify is
      // the source of truth at checkout entry anyway. We just announced what
      // we could.
      if (btn) {
        btn.classList.remove('is-flushing');
        btn.disabled = false;
      }
      form.dataset.cartFlushed = 'true';
      form.submit();
    });
  });
}
